import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  clearAdminSession,
  isSessionTokenExpired,
  readStoredSessionToken,
} from '../../utils/adminSession';

const CHECKIN_POLL_INTERVAL = 5000;
const SOUND_CHANGE_EVENT = 'primefit:sound-change';
const CHECKIN_TOAST_EVENT = 'primefit:checkin-toast';

export function useCheckinNotifications(options = {}) {
  const { poll = true, listenDirect = true } = options;
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const storedValue = localStorage.getItem('soundEnabled');
    return storedValue !== 'false';
  });
  const soundEnabledRef = useRef(soundEnabled);
  const lastNotificationRef = useRef({ signature: '', at: 0 });

  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
    localStorage.setItem('soundEnabled', String(soundEnabled));
  }, [soundEnabled]);

  useEffect(() => {
    const syncSoundPreference = () => {
      const storedValue = localStorage.getItem('soundEnabled');
      const nextValue = storedValue !== 'false';
      soundEnabledRef.current = nextValue;
      setSoundEnabled(nextValue);
    };

    window.addEventListener(SOUND_CHANGE_EVENT, syncSoundPreference);

    return () => {
      window.removeEventListener(SOUND_CHANGE_EVENT, syncSoundPreference);
    };
  }, []);

  const speak = useCallback(data => {
    if (!soundEnabledRef.current || !('speechSynthesis' in window)) {
      return;
    }

    const text =
      data.status === 'success'
        ? `Terima kasih Kak ${data.member}, anda berhasil check-in`
        : `Maaf Kak ${data.member}, anda gagal check-in ${data.statusMessage}`;

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'id-ID';
    utterance.rate = 0.95;
    utterance.pitch = 1.05;
    utterance.volume = 1;

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  }, []);

  const showToast = useCallback(data => {
    if (data.status === 'success') {
      toast.success(`${data.member} berhasil check-in`);
      return;
    }

    toast.error(`${data.member} gagal check-in${data.statusMessage ? `: ${data.statusMessage}` : ''}`);
  }, []);

  const normalizeNotification = useCallback(payload => {
    if (!payload) {
      return null;
    }

    const status =
      payload.status ||
      (payload.checkin_status === 1 ? 'success' : 'failed');

    return {
      id: payload.id ?? null,
      member: payload.member || payload.member_name || 'Member',
      status,
      statusMessage: payload.statusMessage || payload.description || '',
    };
  }, []);

  const notify = useCallback(payload => {
    const normalizedData = normalizeNotification(payload);
    if (!normalizedData?.member) {
      return;
    }

    const notificationSignature = `${normalizedData.member}-${normalizedData.status}-${normalizedData.statusMessage}`;
    const now = Date.now();

    if (
      lastNotificationRef.current.signature === notificationSignature &&
      now - lastNotificationRef.current.at < CHECKIN_POLL_INTERVAL * 2
    ) {
      return;
    }

    lastNotificationRef.current = {
      signature: notificationSignature,
      at: now,
    };
    showToast(normalizedData);
    speak(normalizedData);
  }, [normalizeNotification, showToast, speak]);

  const toggleSound = () => {
    const nextValue = !soundEnabledRef.current;
    setSoundEnabled(nextValue);
    soundEnabledRef.current = nextValue;
    localStorage.setItem('soundEnabled', String(nextValue));
    window.dispatchEvent(new Event(SOUND_CHANGE_EVENT));

    if (!nextValue && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }

    if (nextValue && 'speechSynthesis' in window) {
      const activationUtterance = new SpeechSynthesisUtterance('Notifikasi suara aktif');
      activationUtterance.lang = 'id-ID';
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(activationUtterance);
    }

    toast.info(nextValue ? 'Voice notification aktif' : 'Voice notification dimatikan');
  };

  useEffect(() => {
    if (!listenDirect) {
      return undefined;
    }

    const handleDirectNotification = event => {
      notify(event.detail);
    };

    window.addEventListener(CHECKIN_TOAST_EVENT, handleDirectNotification);

    return () => {
      window.removeEventListener(CHECKIN_TOAST_EVENT, handleDirectNotification);
    };
  }, [listenDirect, notify]);

  useEffect(() => {
    if (!poll) {
      return undefined;
    }

    const redirectToLogin = () => {
      const currentPath = window.location.pathname || '';
      if (currentPath === '/' || currentPath === '/loginadmin' || currentPath === '/logout') {
        return;
      }
      window.location.replace('/loginadmin');
    };

    const markAsRead = async (id, token) => {
      try {
        await fetch(`${process.env.REACT_APP_API_BASE_URL}admin/updatecheckinlast`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ id }),
        });
      } catch (error) {
        console.error('Mark read error:', error);
      }
    };

    const fetchLastCheckin = async () => {
      try {
        const token = readStoredSessionToken();
        if (!token) {
          return;
        }
        if (isSessionTokenExpired(token)) {
          clearAdminSession();
          redirectToLogin();
          return;
        }

        const checkinUrl = new URL(`${process.env.REACT_APP_API_BASE_URL}admin/getcheckinlast`);
        checkinUrl.searchParams.set('_', String(Date.now()));

        const response = await fetch(checkinUrl.toString(), {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          cache: 'no-store',
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            clearAdminSession();
            redirectToLogin();
          }
          return;
        }

        const apiData = await response.json();
        if (!apiData?.id) {
          return;
        }

        notify(apiData);
        await markAsRead(apiData.id, token);
      } catch (error) {
        console.error('Polling error:', error);
      }
    };

    fetchLastCheckin();

    const handleFocus = () => {
      fetchLastCheckin();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchLastCheckin();
      }
    };

    const intervalId = window.setInterval(fetchLastCheckin, CHECKIN_POLL_INTERVAL);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [notify, poll]);

  return {
    soundEnabled,
    toggleSound,
  };
}
